import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PeriodPicker from "@/components/PeriodPicker";
import { formatIDR, currentPeriod, prevPeriod, periodLabel } from "@/lib/format";
import { toast } from "sonner";
import { CopyPlus, Save, PlusCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const fetcher = (url) => http.get(url).then((r) => r.data);

function IdrInput({ value, onChange, testid }) {
  const [raw, setRaw] = useState("");
  useEffect(() => {
    setRaw(value ? Number(value).toLocaleString("id-ID") : "");
  }, [value]);
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">Rp</span>
      <Input
        className="pl-9 h-10 tabular text-right bg-white"
        inputMode="numeric"
        value={raw}
        data-testid={testid}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "");
          const n = digits ? parseInt(digits, 10) : 0;
          setRaw(n ? n.toLocaleString("id-ID") : "");
          onChange(n);
        }}
        placeholder="0"
      />
    </div>
  );
}

export default function InputBulanan() {
  const [period, setPeriod] = useState(currentPeriod());
  const { data: cats } = useSWR("/categories", fetcher);
  const { data: assets, mutate: mutAssets } = useSWR("/assets", fetcher);
  const { data: snaps, mutate: mutSnaps } = useSWR(`/snapshots?period=${period}`, fetcher);

  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newAsset, setNewAsset] = useState({ name: "", category_id: "", notes: "" });

  useEffect(() => {
    const m = {};
    (snaps || []).forEach((s) => (m[s.asset_id] = s.value));
    setValues(m);
  }, [snaps]);

  const grouped = useMemo(() => {
    const map = {};
    (cats || []).forEach((c) => (map[c.id] = { cat: c, items: [] }));
    (assets || []).forEach((a) => {
      if (!map[a.category_id]) return;
      map[a.category_id].items.push(a);
    });
    return Object.values(map);
  }, [cats, assets]);

  const total = useMemo(
    () => Object.values(values).reduce((a, b) => a + Number(b || 0), 0),
    [values]
  );

  const saveAll = async () => {
    const entries = Object.entries(values)
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .map(([asset_id, value]) => ({ asset_id, period, value: Number(value) || 0 }));
    if (entries.length === 0) {
      toast.error("Belum ada nilai yang diisi");
      return;
    }
    setSaving(true);
    try {
      await http.post("/snapshots/bulk", { period, entries });
      toast.success(`Tersimpan ${entries.length} nilai untuk ${periodLabel(period)}`);
      await mutSnaps();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const doRollover = async () => {
    const from = prevPeriod(period);
    try {
      const { data } = await http.post("/snapshots/rollover", {
        from_period: from,
        to_period: period,
      });
      toast.success(`Menduplikasi ${data.count} nilai dari ${periodLabel(from)}`);
      await mutSnaps();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const addAsset = async () => {
    if (!newAsset.name.trim() || !newAsset.category_id) {
      toast.error("Nama dan kategori wajib diisi");
      return;
    }
    try {
      await http.post("/assets", newAsset);
      toast.success("Aset ditambahkan");
      setNewAsset({ name: "", category_id: "", notes: "" });
      setAddOpen(false);
      await mutAssets();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Pencatatan</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Input Bulanan
          </h1>
          <p className="mt-1 text-slate-600">Perbarui saldo tiap aset untuk periode terpilih.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <PeriodPicker period={period} onChange={setPeriod} testidPrefix="input-period" />
          <Button
            variant="outline"
            className="h-10 gap-2 border-slate-200"
            onClick={doRollover}
            data-testid="rollover-button"
          >
            <CopyPlus className="w-4 h-4" /> Salin bulan lalu
          </Button>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="h-10 gap-2 border-slate-200" data-testid="quick-add-asset-button">
                <PlusCircle className="w-4 h-4" /> Tambah aset
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="quick-add-asset-modal">
              <DialogHeader>
                <DialogTitle>Tambah Aset Baru</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Nama</Label>
                  <Input
                    value={newAsset.name}
                    onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
                    placeholder="Contoh: BCA Tabungan"
                    data-testid="quick-add-asset-name"
                  />
                </div>
                <div>
                  <Label>Kategori</Label>
                  <Select
                    value={newAsset.category_id}
                    onValueChange={(v) => setNewAsset({ ...newAsset, category_id: v })}
                  >
                    <SelectTrigger data-testid="quick-add-asset-category">
                      <SelectValue placeholder="Pilih kategori" />
                    </SelectTrigger>
                    <SelectContent>
                      {(cats || []).map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Catatan (opsional)</Label>
                  <Input
                    value={newAsset.notes}
                    onChange={(e) => setNewAsset({ ...newAsset, notes: e.target.value })}
                    placeholder="Catatan"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={addAsset} className="bg-blue-600 hover:bg-blue-700" data-testid="quick-add-asset-submit">
                  Simpan
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button onClick={saveAll} disabled={saving} className="h-10 gap-2 bg-blue-600 hover:bg-blue-700" data-testid="save-all-button">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan…" : "Simpan"}
          </Button>
        </div>
      </header>

      <Card className="p-6 flex items-center justify-between card-hover" data-testid="input-total-card">
        <div>
          <div className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500">Total tercatat</div>
          <div className="mt-1 font-display text-3xl font-semibold tracking-tighter text-slate-900 tabular">
            {formatIDR(total)}
          </div>
        </div>
        <div className="text-right text-xs text-slate-500">
          Periode: <span className="font-medium text-slate-700">{periodLabel(period)}</span>
        </div>
      </Card>

      <div className="grid gap-6">
        {grouped.length === 0 && (
          <Card className="p-10 border-dashed text-center text-slate-500">Belum ada kategori.</Card>
        )}
        {grouped.map((g) => (
          <Card key={g.cat.id} className="overflow-hidden card-hover" data-testid={`category-block-${g.cat.id}`}>
            <div className="px-6 py-4 flex items-center gap-3 border-b border-slate-100">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: g.cat.color }} />
              <h3 className="font-display text-lg font-medium text-slate-900">{g.cat.name}</h3>
              <span className="ml-auto text-xs text-slate-500 tabular">
                {g.items.length} aset
              </span>
            </div>
            <div className="divide-y divide-slate-100">
              {g.items.length === 0 && (
                <div className="px-6 py-6 text-sm text-slate-500">
                  Belum ada aset di kategori ini. Tambahkan via tombol "Tambah aset" atau di halaman Kategori & Aset.
                </div>
              )}
              {g.items.map((a) => (
                <div key={a.id} className="px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800">{a.name}</div>
                    {a.notes && <div className="text-xs text-slate-500">{a.notes}</div>}
                  </div>
                  <div className="w-full sm:w-64">
                    <IdrInput
                      value={values[a.id] || 0}
                      onChange={(v) => setValues({ ...values, [a.id]: v })}
                      testid={`asset-input-${a.id}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
